// ============================================================================
// Microsoft sign-in via the Office-native dialog API (COOP-safe).
//
// WHY: the previous flow used MSAL `loginPopup()`, which internally calls
// `acquireTokenPopupAsync` and polls `popup.closed` to detect cancellation. Inside an
// Office Add-in taskpane (an embedded WebView) that cross-window property access is blocked:
//     "Cross-Origin-Opener-Policy policy would block the window.closed call"
// We cannot fix that server-side — we send no COOP header at all; the policy comes from
// Microsoft's login pages and the WebView host. So the fix has to be client-side, and the
// officially-supported pattern for Office Add-ins is Office.context.ui.displayDialogAsync:
// Office owns the dialog, so there is no cross-window `window.closed` polling at all.
//
// DESIGN NOTES (deliberate, please read before changing):
//  • The dialog opens the SAME page on the SAME origin with ?chattyAuthDialog=1. Reusing the
//    existing taskpane URL avoids adding a new HTML entry point to excel/word/powerpoint builds,
//    and same-origin is REQUIRED for Office.context.ui.messageParent() to work.
//  • MSAL's redirectUri stays `window.location.origin` — exactly what is used (and registered in
//    Azure) today. Keeping it identical means NO Azure app-registration change is needed.
//  • Because that redirect drops the query string, dialog-mode is remembered in **sessionStorage**,
//    which is per-window: it survives the redirect round-trip inside the dialog window and is
//    invisible to the taskpane window.
//  • Everything is best-effort: any failure returns null so the caller can fall back to the old
//    loginPopup path. Worst case is today's behaviour, never worse.
// ============================================================================

const AUTH_DIALOG_PARAM = "chattyAuthDialog";
const AUTH_DIALOG_FLAG = "chatty_auth_dialog_active";
const DIALOG_TIMEOUT_MS = 3 * 60 * 1000; // don't hang forever if the dialog is abandoned
const DIALOG_FLAG_TTL_MS = 5 * 60 * 1000; // dialog-mode flag self-expires (see isAuthDialogWindow)
const MSAL_SCOPES = ["openid", "profile", "email"];

// Office is injected by office.js at runtime; the chatty package has no @types/office-js.
function office(): any {
  return (globalThis as unknown as { Office?: any }).Office;
}

/** True when Office.js is present AND the dialog API is usable (i.e. running inside Office). */
export function isOfficeDialogSupported(): boolean {
  try {
    const o = office();
    return !!(o && o.context && o.context.ui && typeof o.context.ui.displayDialogAsync === "function");
  } catch {
    return false;
  }
}

/**
 * True when THIS window is the auth dialog (either the initial load, or after the MS redirect).
 *
 * SAFETY: the sessionStorage flag is stored with a timestamp and only honoured for a short window.
 * sessionStorage is per-window, so it should never be visible to the taskpane — but if some WebView
 * host ever shared a storage partition, a stuck flag would make the TASKPANE short-circuit into
 * dialog mode and never render the app. The expiry makes that failure self-healing instead of
 * permanent, and the flag is cleared as soon as it is seen to be stale.
 */
export function isAuthDialogWindow(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get(AUTH_DIALOG_PARAM) === "1") return true;
    const raw = window.sessionStorage.getItem(AUTH_DIALOG_FLAG);
    if (!raw) return false;
    const startedAt = Number(raw);
    if (Number.isFinite(startedAt) && Date.now() - startedAt < DIALOG_FLAG_TTL_MS) return true;
    window.sessionStorage.removeItem(AUTH_DIALOG_FLAG); // stale → never trap the taskpane
    return false;
  } catch {
    return false;
  }
}

function buildMsalConfig(clientId: string) {
  // Identical to the pre-existing loginPopup config — same clientId, authority and redirectUri,
  // so no Azure redirect-URI registration change is required.
  return {
    auth: {
      clientId,
      authority: "https://login.microsoftonline.com/common",
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "localStorage" as const },
  };
}

function messageParent(payload: Record<string, unknown>): void {
  try {
    office()?.context?.ui?.messageParent(JSON.stringify(payload));
  } catch {
    /* nothing more we can do from inside the dialog */
  }
}

/**
 * DIALOG SIDE. Runs inside the Office dialog window. Completes the Microsoft redirect flow and
 * hands the id_token back to the taskpane via messageParent(). Never throws.
 */
export async function runAuthDialogFlow(clientId: string | null | undefined): Promise<void> {
  try {
    window.sessionStorage.setItem(AUTH_DIALOG_FLAG, String(Date.now()));
  } catch {
    /* private mode — flow still works on the first pass */
  }

  if (!clientId) {
    messageParent({ ok: false, error: "microsoft_sso_not_configured" });
    return;
  }

  try {
    const o = office();
    if (o?.onReady) await o.onReady();

    const { PublicClientApplication } = await import("@azure/msal-browser");
    const msal = new PublicClientApplication(buildMsalConfig(clientId));
    await msal.initialize();

    // Second pass: we've come back from Microsoft with the auth response.
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult) {
      try {
        window.sessionStorage.removeItem(AUTH_DIALOG_FLAG);
      } catch { /* ignore */ }
      if (redirectResult.idToken) messageParent({ ok: true, idToken: redirectResult.idToken });
      else messageParent({ ok: false, error: "invalid_microsoft_token" });
      return;
    }

    // First pass: start the redirect. No popup is opened, so COOP never applies.
    await msal.loginRedirect({ scopes: MSAL_SCOPES, prompt: "select_account" });
  } catch (err) {
    try {
      window.sessionStorage.removeItem(AUTH_DIALOG_FLAG);
    } catch { /* ignore */ }
    messageParent({
      ok: false,
      error: err instanceof Error ? err.message : "microsoft_login_failed",
    });
  }
}

/**
 * TASKPANE SIDE. Opens the Office dialog and resolves with the Microsoft id_token.
 * Returns null when the dialog route is unavailable or fails, so the caller can fall back to
 * the legacy loginPopup path. Throws ONLY on explicit user cancellation, so we don't immediately
 * re-open a second popup in the user's face.
 */
export class AuthDialogCancelled extends Error {
  constructor() {
    super("microsoft_login_cancelled");
    this.name = "AuthDialogCancelled";
  }
}

export function acquireMicrosoftIdTokenViaOfficeDialog(): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (!isOfficeDialogSupported()) {
      resolve(null); // not inside Office → caller falls back
      return;
    }

    let settled = false;
    let dialog: any = null;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      try { dialog?.close(); } catch { /* already closed */ }
      clearTimeout(timer);
      fn();
    };

    const timer = setTimeout(() => finish(() => resolve(null)), DIALOG_TIMEOUT_MS);

    // Same page, same origin, flagged as the auth dialog.
    const url = `${window.location.origin}${window.location.pathname}?${AUTH_DIALOG_PARAM}=1`;

    try {
      office().context.ui.displayDialogAsync(
        url,
        { height: 60, width: 30, promptBeforeOpen: false },
        (result: any) => {
          const o = office();
          if (!result || result.status !== o.AsyncResultStatus.Succeeded) {
            finish(() => resolve(null)); // couldn't even open → fall back
            return;
          }
          dialog = result.value;

          dialog.addEventHandler(o.EventType.DialogMessageReceived, (arg: any) => {
            let payload: any = null;
            try { payload = JSON.parse(arg?.message ?? "{}"); } catch { payload = null; }
            if (payload?.ok && payload.idToken) finish(() => resolve(payload.idToken as string));
            else finish(() => resolve(null)); // dialog reported failure → fall back
          });

          dialog.addEventHandler(o.EventType.DialogEventReceived, (arg: any) => {
            // 12006 = user closed the dialog manually. Treat as a deliberate cancel rather than
            // falling back, so we never surprise them with a second popup.
            if (arg?.error === 12006) finish(() => reject(new AuthDialogCancelled()));
            else finish(() => resolve(null));
          });
        },
      );
    } catch {
      finish(() => resolve(null));
    }
  });
}
